'use client'

import { Input } from '@/components/forms/inputs/text/Input'
import { Badge } from '@/components/ui/common/Badge'
import { Button } from '@/components/ui/common/Button'
import { cn } from '@/utils/common'
import {
  AlertCircle,
  Beaker,
  BookOpen,
  Calculator,
  CheckCircle,
  Globe,
  History,
  Info,
  LucideProps,
  Search,
  Target,
  Users
} from 'lucide-react'
import { useState } from 'react'
interface Subject {
  id: number
  name: string
  level: 'Podstawowy' | 'Rozszerzony'
  difficulty: 'Łatwy' | 'Średni' | 'Trudny'
  popularity: number
  averageScore: number
  description: string
  topics: string[]
  examTips: string[]
  requiredFor: string[]
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, "ref">>
  color: string
  isRecommended: boolean
  timeToStudy: string
}

export default function GraduateLessons() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedLevel, setSelectedLevel] = useState('Wszystkie')
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null)

  const subjects: Subject[] = [
    {
      id: 1,
      name: 'Matematyka',
      level: 'Rozszerzony',
      difficulty: 'Trudny',
      popularity: 85,
      averageScore: 68,
      description:
        'Matematyka rozszerzona obejmuje zaawansowane zagadnienia z algebry, geometrii, analizy matematycznej i statystyki.',
      topics: [
        'Funkcje i ich własności',
        'Równania i nierówności',
        'Ciągi i szeregi',
        'Geometria analityczna',
        'Rachunek różniczkowy',
        'Rachunek całkowy',
        'Kombinatoryka i prawdopodobieństwo',
        'Statystyka'
      ],
      examTips: [
        'Systematycznie rozwiązuj zadania z lat poprzednich',
        'Opanuj wzory i naucz się ich stosowania',
        'Ćwicz zadania tekstowe i praktyczne zastosowania',
        'Zwróć uwagę na dokładność obliczeń'
      ],
      requiredFor: ['Informatyka', 'Inżynieria', 'Ekonomia', 'Fizyka', 'Architektura'],
      icon: Calculator,
      color: 'blue',
      isRecommended: true,
      timeToStudy: '2-3 godziny dziennie'
    },
    {
      id: 2,
      name: 'Język angielski',
      level: 'Rozszerzony',
      difficulty: 'Średni',
      popularity: 92,
      averageScore: 75,
      description:
        'Egzamin z języka angielskiego na poziomie rozszerzonym sprawdza zaawansowane umiejętności językowe.',
      topics: [
        'Rozumienie ze słuchu',
        'Rozumienie tekstów pisanych',
        'Znajomość środków językowych',
        'Wypowiedź pisemna',
        'Wypowiedź ustna',
        'Słownictwo zaawansowane',
        'Gramatyka',
        'Kultura krajów anglojęzycznych'
      ],
      examTips: [
        'Czytaj książki i artykuły w języku angielskim',
        'Oglądaj filmy i seriale z napisami',
        'Ćwicz pisanie esejów i listów formalnych',
        'Rozmawiaj z native speakerami'
      ],
      requiredFor: [
        'Filologia angielska',
        'Stosunki międzynarodowe',
        'Biznes',
        'Turystyka'
      ],
      icon: Globe,
      color: 'green',
      isRecommended: true,
      timeToStudy: '1-2 godziny dziennie'
    },
    {
      id: 3,
      name: 'Biologia',
      level: 'Rozszerzony',
      difficulty: 'Średni',
      popularity: 78,
      averageScore: 72,
      description:
        'Biologia rozszerzona obejmuje szczegółowe zagadnienia z biologii molekularnej, genetyki, ekologii i ewolucji.',
      topics: [
        'Budowa i funkcje komórki',
        'Genetyka molekularna',
        'Metabolizm',
        'Rozmnażanie i rozwój',
        'Ewolucja',
        'Ekologia',
        'Anatomia i fizjologia człowieka',
        'Biotechnologia'
      ],
      examTips: [
        'Naucz się rysować i opisywać schematy',
        'Zrozum procesy biochemiczne',
        'Ćwicz analizę wyników eksperymentów',
        'Ucz się systematycznie, nie na ostatnią chwilę'
      ],
      requiredFor: [
        'Medycyna',
        'Biologia',
        'Biotechnologia',
        'Psychologia',
        'Weterynaria'
      ],
      icon: Beaker,
      color: 'emerald',
      isRecommended: false,
      timeToStudy: '1.5-2 godziny dziennie'
    },
    {
      id: 4,
      name: 'Chemia',
      level: 'Rozszerzony',
      difficulty: 'Trudny',
      popularity: 65,
      averageScore: 65,
      description:
        'Chemia rozszerzona to zaawansowane zagadnienia z chemii nieorganicznej, organicznej i fizycznej.',
      topics: [
        'Budowa atomu i układ okresowy',
        'Wiązania chemiczne',
        'Termodynamika chemiczna',
        'Kinetyka chemiczna',
        'Równowaga chemiczna',
        'Chemia organiczna',
        'Elektrochemia',
        'Chemia analityczna'
      ],
      examTips: [
        'Naucz się równań reakcji chemicznych',
        'Ćwicz obliczenia stechiometryczne',
        'Zrozum mechanizmy reakcji',
        'Praktykuj zadania laboratoryjne'
      ],
      requiredFor: ['Medycyna', 'Chemia', 'Farmacja', 'Inżynieria chemiczna'],
      icon: Beaker,
      color: 'purple',
      isRecommended: false,
      timeToStudy: '2-2.5 godziny dziennie'
    },
    {
      id: 5,
      name: 'Fizyka',
      level: 'Rozszerzony',
      difficulty: 'Trudny',
      popularity: 58,
      averageScore: 62,
      description:
        'Fizyka rozszerzona obejmuje mechanikę, termodynamikę, elektryczność, optykę i fizykę współczesną.',
      topics: [
        'Mechanika klasyczna',
        'Termodynamika',
        'Elektryczność i magnetyzm',
        'Drgania i fale',
        'Optyka',
        'Fizyka atomowa',
        'Fizyka jądrowa',
        'Teoria względności'
      ],
      examTips: [
        'Naucz się wyprowadzać wzory',
        'Ćwicz zadania obliczeniowe',
        'Zrozum zjawiska fizyczne',
        'Analizuj wykresy i diagramy'
      ],
      requiredFor: ['Fizyka', 'Inżynieria', 'Informatyka', 'Astronomia'],
      icon: Target,
      color: 'red',
      isRecommended: false,
      timeToStudy: '2-3 godziny dziennie'
    },
    {
      id: 6,
      name: 'Historia',
      level: 'Rozszerzony',
      difficulty: 'Średni',
      popularity: 70,
      averageScore: 70,
      description:
        'Historia rozszerzona obejmuje szczegółową analizę procesów historycznych od starożytności do współczesności.',
      topics: [
        'Historia starożytna',
        'Średniowiecze',
        'Nowożytność',
        'Historia Polski',
        'Historia powszechna XIX w.',
        'XX wiek',
        'Historia najnowsza',
        'Metodologia historii'
      ],
      examTips: [
        'Ucz się dat i faktów systematycznie',
        'Analizuj źródła historyczne',
        'Ćwicz pisanie rozprawek',
        'Łącz wydarzenia w logiczne ciągi przyczynowo-skutkowe'
      ],
      requiredFor: ['Historia', 'Politologia', 'Stosunki międzynarodowe', 'Prawo'],
      icon: History,
      color: 'amber',
      isRecommended: false,
      timeToStudy: '1.5-2 godziny dziennie'
    }
  ]

  const levels = ['Wszystkie', 'Podstawowy', 'Rozszerzony']

  const filteredSubjects = subjects.filter((subject) => {
    const matchesSearch = subject.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesLevel = selectedLevel === 'Wszystkie' || subject.level === selectedLevel
    return matchesSearch && matchesLevel
  })

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'Łatwy':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'Średni':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'Trudny':
        return 'text-red-600 bg-red-50 border-red-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  if (selectedSubject) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <button
              className="mb-4 flex items-center text-[#2db2b8] hover:text-[#2db2b8]/80"
              onClick={() => setSelectedSubject(null)}
            >
              <Target className="mr-1 h-4 w-4 rotate-180 transform" />
              Powrót do listy przedmiotów
            </button>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <selectedSubject.icon className="mr-3 h-8 w-8 text-[#2db2b8]" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {selectedSubject.name}
                  </h1>
                  <p className="mt-1 text-gray-600">
                    Poziom {selectedSubject.level.toLowerCase()}
                  </p>
                </div>
              </div>
              {selectedSubject.isRecommended && (
                <Badge className="border-green-200 bg-green-100 text-green-800">
                  <CheckCircle className="mr-1 h-3 w-3" />
                  Polecany
                </Badge>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column */}
            <div className="space-y-6 lg:col-span-2">
              {/* Description */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Opis przedmiotu</h2>
                <p className="leading-relaxed text-gray-700">
                  {selectedSubject.description}
                </p>
              </div>

              {/* Topics */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Zakres materiału</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {selectedSubject.topics.map((topic, index) => (
                    <div
                      className="flex items-center border border-gray-200 bg-gray-50 p-3"
                      key={index}
                    >
                      <BookOpen className="mr-2 h-4 w-4 flex-shrink-0 text-[#2bcaca]" />
                      <span className="text-sm text-gray-700">{topic}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Exam Tips */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Wskazówki do nauki</h2>
                <div className="space-y-3">
                  {selectedSubject.examTips.map((tip, index) => (
                    <div
                      className="flex items-start border border-gray-200 bg-gray-50 p-3"
                      key={index}
                    >
                      <Info className="mt-0.5 mr-2 h-4 w-4 flex-shrink-0 text-[#2bcaca]" />
                      <span className="text-sm text-gray-700">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Required For */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Wymagany dla kierunków</h2>
                <div className="flex flex-wrap gap-2">
                  {selectedSubject.requiredFor.map((field, index) => (
                    <Badge
                      className="rounded-sm border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca]"
                      key={index}
                      variant="outline"
                    >
                      {field}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Statystyki</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Średni wynik</span>
                    <span className="text-lg font-bold">
                      {selectedSubject.averageScore}%
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Popularność</span>
                    <div className="flex items-center">
                      <div className="mr-2 h-2 w-16 rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-[#2bcaca]"
                          style={{ width: `${selectedSubject.popularity}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium">
                        {selectedSubject.popularity}%
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Trudność</span>
                    <Badge
                      className={getDifficultyColor(selectedSubject.difficulty)}
                      variant="outline"
                    >
                      {selectedSubject.difficulty}
                    </Badge>
                  </div>

                  <div className="border-t pt-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Czas nauki dziennie</span>
                      <span className="text-sm font-medium">
                        {selectedSubject.timeToStudy}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Study Plan */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Plan przygotowań</h2>
                <div className="space-y-3">
                  <div className="flex items-center border border-gray-200 bg-gray-50 p-3">
                    <CheckCircle className="mr-2 h-4 w-4 text-[#2bcaca]" />
                    <span className="text-sm">12 miesięcy przed maturą</span>
                  </div>
                  <div className="flex items-center border border-gray-200 bg-gray-50 p-3">
                    <AlertCircle className="mr-2 h-4 w-4 text-[#2bcaca]" />
                    <span className="text-sm">6 miesięcy przed maturą</span>
                  </div>
                  <div className="flex items-center border border-gray-200 bg-gray-50 p-3">
                    <Target className="mr-2 h-4 w-4 text-[#2bcaca]" />
                    <span className="text-sm">1 miesiąc przed maturą</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="border border-gray-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Działania</h2>
                <div className="space-y-3">
                  <Button className="w-full rounded-sm bg-[#2bcaca] text-white hover:bg-[#229999]">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Plan nauki
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <Target className="mr-2 h-4 w-4" />
                    Arkusze maturalne
                  </Button>
                  <Button className="w-full rounded-sm" variant="outline">
                    <Users className="mr-2 h-4 w-4" />
                    Znajdź korepetytora
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">Przedmioty maturalne</h1>
          <p className="text-gray-600">
            Sprawdź wymagania i przygotuj się do egzaminów maturalnych
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  className="border-gray-200 pl-10"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Szukaj przedmiotu..."
                  value={searchTerm}
                />
              </div>
            </div>

            {/* Level Filter */}
            <div className="flex flex-wrap gap-2">
              {levels.map((level) => (
                <Button
                  className={cn(
                    selectedLevel === level
                      ? 'bg-[#2bcaca] text-white hover:bg-[#229999]'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                  key={level}
                  onClick={() => setSelectedLevel(level)}
                  size="sm"
                  variant={selectedLevel === level ? 'default' : 'outline'}
                >
                  {level}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredSubjects.map((subject) => (
            <div
              className="flex h-full cursor-pointer flex-col border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
              key={subject.id}
              onClick={() => setSelectedSubject(subject)}
            >
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="mb-1 font-semibold text-gray-900">{subject.name}</h3>
                  <p className="text-sm text-gray-500">{subject.level}</p>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-lg font-bold text-[#2bcaca]">
                    {subject.popularity}%
                  </div>
                  <p className="text-xs text-gray-500">popularność</p>
                </div>
              </div>

              {/* Description */}
              <p className="mb-4 line-clamp-3 flex-grow text-sm text-gray-700">
                {subject.description}
              </p>

              {/* Stats */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Trudność:</span>
                  <Badge
                    className={getDifficultyColor(subject.difficulty)}
                    size="sm"
                    variant="outline"
                  >
                    {subject.difficulty}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Średni wynik:</span>
                  <span className="font-medium">{subject.averageScore}%</span>
                </div>
              </div>

              {/* Topics Preview */}
              <div className="mb-4">
                <p className="mb-2 text-xs text-gray-500">Tematy:</p>
                <div className="flex flex-wrap gap-1">
                  {subject.topics.slice(0, 3).map((topic, index) => (
                    <Badge
                      className="border-gray-200 text-xs"
                      key={index}
                      size="sm"
                      variant="outline"
                    >
                      {topic}
                    </Badge>
                  ))}
                  {subject.topics.length > 3 && (
                    <Badge
                      className="border-gray-200 text-xs"
                      size="sm"
                      variant="outline"
                    >
                      +{subject.topics.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Action */}
              <Button className="mt-auto w-full rounded-sm" size="sm" variant="outline">
                <BookOpen className="mr-2 h-4 w-4" />
                Zobacz szczegóły
              </Button>
            </div>
          ))}
        </div>

        {filteredSubjects.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              Nie znaleziono przedmiotów spełniających kryteria wyszukiwania.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
